import * as React from "react";
import styles from "./common/Profile.module.css";
import { UserBanner } from "./common/UserBanner";
import { Card, Checkbox, Col, Row, message } from "antd";
import { Link, useNavigate } from "react-router-dom";
import { RightOutlined, WechatOutlined } from "@ant-design/icons";
import { useSelector } from "react-redux";
import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { responseNotification } from "../../utils/notify";

export default function SellerDashboard() {
  const { token } = useSelector((state) => (state as any)?.authReducer);
  const navigate = useNavigate();

  const [getBankType, setGetBankType] = useState("BANKING");
  const [merchantShopCheck, setMerchantShopCheck] = useState<any>();
  const [businessContractCheck, setBusinessContractCheck] = useState<any>();
  const [merchantDetailsInfo, setMerchantDetailsInfo] = useState<any>();
  const [personalDetailsCheck, setPersonalDetailsCheck] = useState<any>();
  const [addBankInfoCheck, setAddBankInfoCheck] = useState<any>();

  const userProfilePic =
    "https://cdn.builder.io/api/v1/image/assets/TEMP/7db1df81aaba85dfcce8c94cab2a88c7306a9d003417116f2be65bc69809587a?placeholderIfAbsent=true&apiKey=61022fe3774e4aa287a6264de6b79c87";

  // CREATE BUSINESS COMPLETE CHECK
  const isBusinessCreated = (merchantShopCheck?.length || 0) > 0;

  const guardNavigation = (path: string) => {
    if (!isBusinessCreated) {
      message.warning("Please complete Create Business first");
      return;
    }
    navigate(path);
  };
  const isPersonalDone = !!personalDetailsCheck?.data;
  const isBusinessContactDone = !!businessContractCheck?.merchantBusinessAndContactInfo?.address;
  const isBusinessInfoDone = !!businessContractCheck?.merchantBusinessInfo;
  const isBankDone = (addBankInfoCheck?.data?.length || 0) > 0;

  const profileCompletion = useMemo(() => {
    const step = 20;
    let total = 0;

    if (isPersonalDone) total += step;           // 1
    if (isBusinessCreated) total += step;        // 2
    if (isBusinessContactDone) total += step;    // 3
    if (isBusinessInfoDone) total += step;       // 4
    if (isBankDone) total += step;              // 5

    return Math.min(100, total);
  }, [isPersonalDone, isBusinessCreated, isBusinessContactDone, isBusinessInfoDone, isBankDone]);

  const getMerchantShopCheckFn = useCallback(async () => {
    const res = await axios.get(
      `${process.env.REACT_APP_CATALOG_READER_API}/merchant/all-shops-by-merchant`,
      {
        headers: { Authorization: `Bearer ${token}` },
      }
    );
    setMerchantShopCheck(res?.data?.shops);
  }, [token]);
  const getBusinessContractCheck = useCallback(async () => {
    if (!merchantShopCheck || merchantShopCheck.length === 0) return;
    const res = await axios.get(
      `${process.env.REACT_APP_CATALOG_READER_API}/merchant-shop/my-shop-details?shopId=${merchantShopCheck[0].id}`,
      {
        headers: { Authorization: `Bearer ${token}` },
      }
    );
    setBusinessContractCheck(res?.data?.shop);
  }, [merchantShopCheck, token]);
  const fetchMerchantDetails = useCallback(() => {
    try {
      setPersonalDetailsCheck({ loading: true, data: null });
      axios
        .get(`${process.env.REACT_APP_RIDER_API}/details`, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        })
        .then((data) => {
          if (data.status === 200) {
            setPersonalDetailsCheck({
              loading: false,
              data: data?.data?.merchant?.merchantPersonalDetail,
            });
            setMerchantDetailsInfo(data?.data?.merchant);
          } else {
            setPersonalDetailsCheck({ loading: false, data: null });
            responseNotification(data.statusText || "something went wrong", "error");
          }
        })
        .catch(() => {
          setPersonalDetailsCheck({ loading: false, data: null });
        });
    } catch {
      setPersonalDetailsCheck({ loading: false, data: null });
    }
  }, [token]);
  const fetchMerchantBankDetails = useCallback(() => {
    if (!isBusinessCreated) return;
    try {
      setAddBankInfoCheck({ loading: true, data: null });
      axios
        .get(
          `${process.env.REACT_APP_CATALOG_WRITER_API}/shop-bank/getAllBank?shopId=${merchantShopCheck[0].id}&bankingType=${getBankType}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        )
        .then((data) => {
          if (data.status === 200) {
            setAddBankInfoCheck({
              loading: false,
              data: data?.data?.shopBanks,
            });
          } else {
            setAddBankInfoCheck({ loading: false, data: null });
            responseNotification(data.statusText || "something went wrong", "error");
          }
        })
        .catch(() => {
          setAddBankInfoCheck({ loading: false, data: null });
        });
    } catch {
      setAddBankInfoCheck({ loading: false, data: null });
    }
  }, [isBusinessCreated, merchantShopCheck, getBankType, token]);

  useEffect(() => {
    fetchMerchantDetails();
  }, [fetchMerchantDetails]);

  useEffect(() => {
    getMerchantShopCheckFn();
    fetchMerchantDetails();
  }, [getMerchantShopCheckFn, fetchMerchantDetails]);

  useEffect(() => {
    if (isBusinessCreated) getBusinessContractCheck();
  }, [getBusinessContractCheck, isBusinessCreated]);

  useEffect(() => {
    if (isBusinessCreated) fetchMerchantBankDetails();
  }, [fetchMerchantBankDetails, isBusinessCreated]);

  useEffect(() => {
    if (merchantDetailsInfo?.state === "VERIFIED") {
      navigate("/", { replace: true });
    }
  }, [merchantDetailsInfo?.state, navigate]);

  return (
    <div className="flex overflow-hidden flex-col items-center bg-stone-50 pb-[20px] max-md:pb-24">
      <UserBanner
        userId={merchantDetailsInfo?.walletId || "JM 5421547"}
        userName={merchantDetailsInfo?.fullName || "New User"}
        userProfile={merchantDetailsInfo?.profilePicture || userProfilePic}
        membershipInfo={{
          title: "Welcome to Jachai Seller Center",
          description: "Explore as a new seller and unlock more benefits",
          ctaText: <Link to="/membership-upgrade">Explore </Link>,
        }}
        verificationStatus="Our team is reviewing your documents. Expect a response within 72 hours."
        profileCompletion={profileCompletion} 
      />

      <div className="flex flex-col gap-8 w-[970px] max-w-full mt-8">
        <Row className="w-full">
          <Col className="w-1/2">
            <Card className={styles.cardMainRight}>
              <div className={styles.cardListCheck}>
                <Checkbox checked={isPersonalDone} />
              </div>
              <div className={styles.cardListInfo} onClick={() => navigate("/personal-details")}>
                Personal Details
              </div>
              <div className={styles.cardListArro}>
                <RightOutlined />
              </div>
            </Card>
          </Col>

          <Col className="w-1/2">
            <Card className={styles.cardMainRight}>
              <div className={styles.cardListCheck}>
                <Checkbox checked={isBusinessCreated} />
              </div>
              <div
                className={styles.cardListInfo}
                onClick={() => navigate("/business-create")}
                style={{ cursor: "pointer" }}
              >
                Create Business
              </div>
              <div className={styles.cardListArro}>
                <RightOutlined />
              </div>
            </Card>
          </Col>
        </Row>

        {/* BUSINESS & CONTACT + BUSINESS INFO */}
        <Row className="w-full">
          <Col className="w-1/2">
            <Card className={styles.cardMain}>
              <div className={styles.cardListCheck}>
                <Checkbox checked={isBusinessContactDone} />
              </div>

              <div
                className={styles.cardListInfo}
                onClick={() => guardNavigation("/business-contract-address")}
                style={{
                  cursor: isBusinessCreated ? "pointer" : "not-allowed",
                  color: isBusinessCreated ? "inherit" : "gray",
                }}
              >
                Business & Contact Address
              </div>

              <div className={styles.cardListArro}>
                <RightOutlined />
              </div>
            </Card>
          </Col>

          <Col className="w-1/2">
            <Card className={styles.cardMainRight}>
              <div className={styles.cardListCheck}>
                <Checkbox checked={isBusinessInfoDone} />
              </div>

              <div
                className={styles.cardListInfo}
                onClick={() => guardNavigation("/business-day-info")}
                style={{
                  cursor: isBusinessCreated ? "pointer" : "not-allowed",
                  color: isBusinessCreated ? "inherit" : "gray",
                }}
              >
                Business Info
              </div>

              <div className={styles.cardListArro}>
                <RightOutlined />
              </div>
            </Card>
          </Col>
        </Row>

        {/* ADD BANK + LIVE CHAT */}
        <Row className="w-full">
          <Col className="w-1/2">
            <Card className={styles.cardMain}>
              <div className={styles.cardListCheck}>
                <Checkbox checked={isBankDone} />
              </div>

              <div
                className={styles.cardListInfo}
                onClick={() => guardNavigation("/bank-info")}
                style={{
                  cursor: isBusinessCreated ? "pointer" : "not-allowed",
                  color: isBusinessCreated ? "inherit" : "gray",
                }}
              >
                Add Bank
              </div>

              <div className={styles.cardListArro}>
                <RightOutlined />
              </div>
            </Card>
          </Col>

          <Col className="w-1/2">
            <Card className={styles.cardMainRight}>
              <div className={styles.cardListCheck}>
                <WechatOutlined />
              </div>
              <div className={styles.cardListInfo}>
                <Link to="javascript:void(Tawk_API.toggle())">Live Chat</Link>
              </div>
              <div className={styles.cardListArro}>
                <RightOutlined />
              </div>
            </Card>
          </Col>
        </Row>
      </div>

      {/* UNDER REVIEW CARD */}
      {isBusinessCreated &&
        isBusinessContactDone &&
        isBusinessInfoDone &&
        isPersonalDone && (
          <div className="flex flex-col justify-center items-center px-20 py-14 mt-20 max-w-full text-lg font-bold text-center bg-white rounded-xl w-[965px] max-md:px-5 max-md:mt-10">
            <div className="flex flex-col items-center max-w-full w-[471px]">
              <img
                loading="lazy"
                src="https://cdn.builder.io/api/v1/image/assets/TEMP/91b66f6db19d995d1209b942fd7ab8260a6ed4cd28032f81fd08fb5a8af861c6?placeholderIfAbsent=true&apiKey=61022fe3774e4aa287a6264de6b79c87"
                alt="Create business illustration"
                className="object-contain max-w-full aspect-[1.15] w-[164px]"
              />
              <div className="self-stretch mt-5 text-zinc-500 max-md:max-w-full">
                It takes about 2-5 working days.
              </div>
              <button className="px-12 py-3.5 mt-11 max-w-full text-white bg-slate-300 rounded-md w-[330px] max-md:px-5 max-md:mt-10">
                Under Review
              </button>
            </div>
          </div>
        )}
    </div>
  );
}
